import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  LOCAL_DEV_USER,
  Group,
  ICommonAuthResp,
} from '@zero-dash/types';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';

interface IUseGroupListReturn {
  groups: ICommonAuthResp<Group>[];
  loading: boolean;
  error?: string;
  getGroupById: (groupId: string) => Group | null;
}

/** How long a warm directory cache is reused before the next mount refetches it. */
const CACHE_TTL_MS = 5 * 60 * 1000;

/** Delay before a failed directory fetch is automatically re-attempted. */
const FAILED_RETRY_DELAY_MS = 30 * 1000;

/**
 * Process-wide cache of the group directory, shared across all `useGroupList`
 * consumers so concurrent mounts trigger a single `/auth/groups` request. The
 * cache expires after CACHE_TTL_MS so directory changes propagate.
 */
let groupsCache: ICommonAuthResp<Group>[] | null = null;
let groupsCachedAt = 0;
let inFlightGroups: Promise<ICommonAuthResp<Group>[]> | null = null;

const isCacheFresh = (): boolean => (
  groupsCache !== null && Date.now() - groupsCachedAt < CACHE_TTL_MS
);

/**
 * Fetches the group directory on demand and exposes a `getGroupById` helper
 * that resolves a group id to its name. The cache is invalidated after
 * CACHE_TTL_MS, and a failed fetch is retried automatically after
 * FAILED_RETRY_DELAY_MS.
 */
export function useGroupList(): IUseGroupListReturn {
  const zeroDashSdk = useZeroDashSdk();

  const [groups, setGroups] = useState<ICommonAuthResp<Group>[]>(
    isCacheFresh() ? groupsCache ?? [] : [],
  );
  const [loading, setLoading] = useState<boolean>(!isCacheFresh());
  const [error, setError] = useState<string>();
  const [retryToken, setRetryToken] = useState<number>(0);

  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;

    if (import.meta.env.VITE_DISABLE_AUTH === 'true') {
      setGroups(LOCAL_DEV_USER.groups as unknown as ICommonAuthResp<Group>[]);
      setLoading(false);
      return undefined;
    }

    if (isCacheFresh()) {
      setGroups(groupsCache ?? []);
      setLoading(false);
      return undefined;
    }

    async function loadGroups(): Promise<void> {
      setLoading(true);
      setError(undefined);

      try {
        if (!inFlightGroups) {
          inFlightGroups = zeroDashSdk.services.auth
            .getGroups()
            .then((resp) => {
              groupsCache = resp;
              groupsCachedAt = Date.now();
              return resp;
            })
            .finally(() => {
              inFlightGroups = null;
            });
        }

        const result = await inFlightGroups;

        if (isMounted) setGroups(result);
      } catch {
        if (isMounted) {
          setError('An error occurred while fetching groups');
          retryTimerRef.current = setTimeout(
            () => setRetryToken((token) => token + 1),
            FAILED_RETRY_DELAY_MS,
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadGroups();

    return () => {
      isMounted = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [zeroDashSdk.services.auth, retryToken]);

  const getGroupById = useCallback((groupId: string): Group | null => (
    groups.find((group) => group.id === groupId)?.name || null
  ), [groups]);

  return useMemo<IUseGroupListReturn>(() => ({
    groups,
    loading,
    error,
    getGroupById,
  }), [groups, loading, error, getGroupById]);
}
